/**
 * Barra de creación inferior (equivalente a la barra de Microsoft Whiteboard).
 * Al pulsar una herramienta ya activa se abre su panel de color/grosor.
 */
import type { GeoKind } from '../../shared/types.ts'
import {
  FILL_COLORS,
  HIGHLIGHTER_COLORS,
  HIGHLIGHTER_SIZES,
  INK_COLORS,
  NOTE_COLORS,
  PEN_SIZES,
} from '../../shared/palette.ts'
import type { App } from '../app.ts'
import { PRESSURE_LABELS, PRESSURE_THINNING, type PenPressure } from '../state/store.ts'
import { closePopover, h, isPopoverOpenFor, openPopoverAt, toolButton } from './dom.ts'
import { icon } from './icons.ts'
import { addFloating, setMapLayout } from '../mindmap/actions.ts'

const SHAPE_KINDS: { kind: GeoKind; label: string }[] = [
  { kind: 'rectangle', label: 'Rectángulo' },
  { kind: 'roundRectangle', label: 'Rectángulo redondeado' },
  { kind: 'ellipse', label: 'Elipse' },
  { kind: 'triangle', label: 'Triángulo' },
  { kind: 'diamond', label: 'Rombo' },
  { kind: 'hexagon', label: 'Hexágono' },
  { kind: 'star', label: 'Estrella' },
  { kind: 'arrowRight', label: 'Flecha ancha' },
  { kind: 'line', label: 'Línea' },
  { kind: 'arrow', label: 'Flecha' },
]

const REACTIONS = ['👍', '❤️', '✅', '😀', '🎉', '⭐', '❓', '🔥']

export class Toolbar {
  readonly el: HTMLElement
  private app: App
  private buttons = new Map<string, HTMLButtonElement>()

  constructor(app: App) {
    this.app = app
    this.el = h('div', { class: 'toolbar surface' })
    this.build()
    app.store.subscribe(() => this.sync())
  }

  /** Los paneles se abren hacia el lienzo, no hacia fuera de la pantalla. */
  private get panelPlacement(): 'top' | 'bottom' {
    return this.app.store.toolbarTop ? 'bottom' : 'top'
  }

  private build(): void {
    const add = (
      key: string,
      iconName: string,
      title: string,
      onClick: (e: MouseEvent) => void,
      hasSwatch?: () => string | null,
    ): void => {
      const btn = toolButton({ icon: icon(iconName), title, onClick })
      if (hasSwatch) {
        btn.classList.add('has-swatch')
        const swatch = h('span', { class: 'tool-swatch' })
        btn.append(swatch)
      }
      this.buttons.set(key, btn)
      this.el.append(btn)
    }
    const sep = (): void => {
      this.el.append(h('div', { class: 'sep' }))
    }

    add('select', 'select', 'Seleccionar (Alt+S)', () => this.app.setTool('select'))
    add('hand', 'hand', 'Mover el lienzo (H o Espacio + arrastrar)', () => this.app.setTool('hand'))
    add('lasso', 'lasso', 'Lazo (Alt+Q)', () => this.app.setTool('lasso'))
    sep()
    add('draw', 'pen', 'Pluma (Alt+W)', (e) => this.onToolClick('draw', e), () => this.app.store.style.inkColor)
    add(
      'highlighter',
      'highlighter',
      'Resaltador (Alt+H)',
      (e) => this.onToolClick('highlighter', e),
      () => this.app.store.style.highlighterColor,
    )
    add('eraser', 'eraser', 'Borrador (Alt+X)', (e) => this.onToolClick('eraser', e))
    sep()
    add('note', 'note', 'Nota adhesiva', (e) => this.onToolClick('note', e), () => this.app.store.style.noteColor)
    add('text', 'text', 'Texto', (e) => this.onToolClick('text', e), () => this.app.store.style.textColor)
    add('shape', 'shapes', 'Figuras', (e) => this.onToolClick('shape', e), () => this.app.store.style.shapeColor)
    add('image', 'image', 'Imagen', () => this.app.pickImage())
    add('reaction', 'reaction', 'Reacciones', (e) => this.openReactions(e))
    sep()
    add('laser', 'laser', 'Puntero láser (Alt+L)', () => this.app.setTool('laser'))
    add('more', 'template', 'Plantillas y más', (e) => this.openMore(e))
    sep()
    // Solo en pantallas táctiles: interruptor de rechazo de palma a la vista,
    // para encenderlo o apagarlo sin buscarlo en el menú.
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) {
      add('palm', 'palm-guard', 'Rechazo de palma: el dedo solo navega', () => {
        const next = !this.app.store.palmRejection
        this.app.store.setPref('palmRejection', next)
        this.app.toast(
          next
            ? 'Rechazo de palma activado: el dedo solo mueve el lienzo'
            : 'Rechazo de palma apagado: el dedo también selecciona y edita',
        )
      })
    }
    // Solo en mapas mentales: estructura y tema nuevo.
    add('map-topic', 'mindmap', 'Tema flotante nuevo (o doble clic en el lienzo)', () => {
      const center = this.app.viewCenterWorld()
      const topic = addFloating(this.app.board, this.app.getUserName(), center.x, center.y)
      this.app.store.setSelection([topic.id])
      this.app.editText(topic.id, true)
    })
    add('map-layout', 'shapes', 'Estructura del mapa: balanceado, horizontal o vertical', (e) =>
      this.openMapLayout(e.currentTarget as HTMLElement),
    )
    add('dark', 'moon', 'Modo oscuro: lienzo negro, tinta clara', () => {
      this.app.store.setPref('darkMode', !this.app.store.darkMode)
    })
    add('clear', 'trash', 'Borrar todo el tablero…', () => this.app.clearBoard())
    this.sync()
  }

  private onToolClick(tool: 'draw' | 'highlighter' | 'eraser' | 'note' | 'text' | 'shape', e: MouseEvent): void {
    const anchor = e.currentTarget as HTMLButtonElement
    const alreadyActive = this.app.store.tool === tool
    this.app.setTool(tool)
    if (alreadyActive) {
      if (isPopoverOpenFor(anchor)) closePopover()
      else this.openToolPanel(tool, anchor)
    }
  }

  openToolPanel(tool: string, anchor: HTMLElement): void {
    const { store } = this.app
    const panel = h('div', {})

    if (tool === 'draw' || tool === 'highlighter') {
      const isPen = tool === 'draw'
      const colors = isPen ? INK_COLORS : HIGHLIGHTER_COLORS
      const sizes = isPen ? PEN_SIZES : HIGHLIGHTER_SIZES
      const currentColor = isPen ? store.style.inkColor : store.style.highlighterColor
      const currentSize = isPen ? store.style.inkSize : store.style.highlighterSize

      panel.append(h('h4', {}, ['Color']))
      panel.append(
        h(
          'div',
          { class: `swatches ${isPen ? '' : 'notes'}` },
          colors.map((c) =>
            h('button', {
              class: `swatch ${c.hex === currentColor ? 'is-selected' : ''}`,
              style: `background:${c.hex}`,
              title: c.name,
              onclick: () => {
                store.setStyle(isPen ? { inkColor: c.hex } : { highlighterColor: c.hex })
                this.applyToSelection(isPen ? { color: c.hex } : { color: c.hex })
                closePopover()
              },
            }),
          ),
        ),
      )
      panel.append(h('h4', { style: 'margin-top:14px' }, ['Grosor']))
      panel.append(
        h(
          'div',
          { class: 'sizes' },
          sizes.map((size) =>
            h(
              'button',
              {
                class: `size-btn ${size === currentSize ? 'is-selected' : ''}`,
                title: `${size} px`,
                onclick: () => {
                  store.setStyle(isPen ? { inkSize: size } : { highlighterSize: size })
                  this.applyToSelection({ size })
                  // El panel sigue abierto: así se prueban grosores de corrido.
                  this.openToolPanel(tool, anchor)
                },
              },
              [
                h('span', {
                  class: 'size-dot',
                  style: `width:${Math.min(24, 4 + size)}px;height:${Math.min(24, 4 + size)}px;background:${currentColor}`,
                }),
              ],
            ),
          ),
        ),
      )
      if (isPen) {
        // Sensibilidad a la presión del Apple Pencil: "Parejo" quita el afilado
        // de punta, que es lo que hace que el trazo empiece fino y engorde.
        panel.append(h('h4', { style: 'margin-top:14px' }, ['Presión del lápiz']))
        const pressures: PenPressure[] = ['off', 'low', 'normal', 'high']
        panel.append(
          h(
            'div',
            { class: 'role-pills' },
            pressures.map((level) =>
              h(
                'button',
                {
                  class: `pill ${store.style.penPressure === level ? 'is-active' : ''}`,
                  title:
                    level === 'off'
                      ? 'Grosor parejo de punta a punta'
                      : `La presión cambia el grosor (${Math.round(PRESSURE_THINNING[level] * 100)} %)`,
                  onclick: () => {
                    store.setStyle({ penPressure: level })
                    this.openToolPanel(tool, anchor)
                  },
                },
                [PRESSURE_LABELS[level]],
              ),
            ),
          ),
        )
        panel.append(
          h('label', { class: 'menu-item', style: 'margin-top:8px' }, [
            'Mejorar formas dibujadas',
            this.switchEl(store.enhanceShapes, () => {
              store.setPref('enhanceShapes', !store.enhanceShapes)
              closePopover()
            }),
          ]),
        )
      }
    } else if (tool === 'eraser') {
      panel.append(h('h4', {}, ['Modo']))
      const modes: [string, 'stroke' | 'area'][] = [
        ['Trazo completo', 'stroke'],
        ['Parcial', 'area'],
      ]
      panel.append(
        h(
          'div',
          { class: 'role-pills' },
          modes.map(([label, mode]) =>
            h(
              'button',
              {
                class: `pill ${store.style.eraserMode === mode ? 'is-active' : ''}`,
                onclick: () => {
                  store.setStyle({ eraserMode: mode })
                  closePopover()
                },
              },
              [label],
            ),
          ),
        ),
      )
      panel.append(h('h4', { style: 'margin-top:12px' }, ['Tamaño']))
      panel.append(
        h(
          'div',
          { class: 'sizes' },
          [16, 28, 48, 80].map((size) =>
            h(
              'button',
              {
                class: `size-btn ${store.style.eraserSize === size ? 'is-selected' : ''}`,
                onclick: () => {
                  store.setStyle({ eraserSize: size })
                  closePopover()
                },
              },
              [h('span', { class: 'size-dot', style: `width:${size / 4 + 6}px;height:${size / 4 + 6}px` })],
            ),
          ),
        ),
      )
    } else if (tool === 'note') {
      panel.append(h('h4', {}, ['Color de la nota']))
      panel.append(
        h(
          'div',
          { class: 'swatches notes' },
          NOTE_COLORS.map((c) =>
            h('button', {
              class: `swatch ${c.hex === store.style.noteColor ? 'is-selected' : ''}`,
              style: `background:${c.hex}`,
              title: c.name,
              onclick: () => {
                store.setStyle({ noteColor: c.hex })
                this.applyToSelection({ color: c.hex }, 'note')
                closePopover()
              },
            }),
          ),
        ),
      )
      panel.append(
        h(
          'button',
          {
            class: 'menu-item',
            style: 'margin-top:10px',
            onclick: () => {
              closePopover()
              this.app.insertNoteGrid()
            },
          },
          [spanIcon('noteGrid'), 'Insertar cuadrícula de notas'],
        ),
      )
    } else if (tool === 'text') {
      panel.append(h('h4', {}, ['Color del texto']))
      panel.append(
        h(
          'div',
          { class: 'swatches' },
          INK_COLORS.map((c) =>
            h('button', {
              class: `swatch ${c.hex === store.style.textColor ? 'is-selected' : ''}`,
              style: `background:${c.hex}`,
              title: c.name,
              onclick: () => {
                store.setStyle({ textColor: c.hex })
                this.applyToSelection({ color: c.hex }, 'text')
                closePopover()
              },
            }),
          ),
        ),
      )
      panel.append(h('h4', { style: 'margin-top:14px' }, ['Tamaño']))
      panel.append(
        h(
          'div',
          { class: 'sizes' },
          [18, 24, 32, 48, 72].map((size) =>
            h(
              'button',
              {
                class: `size-btn ${store.style.textSize === size ? 'is-selected' : ''}`,
                onclick: () => {
                  store.setStyle({ textSize: size })
                  this.applyToSelection({ fontSize: size }, 'text')
                  closePopover()
                },
              },
              [h('span', { style: `font-size:${Math.min(18, size / 3)}px;font-weight:600` }, [String(size)])],
            ),
          ),
        ),
      )
    } else if (tool === 'shape') {
      panel.append(h('h4', {}, ['Figura']))
      panel.append(
        h(
          'div',
          { class: 'shape-grid' },
          SHAPE_KINDS.map(({ kind, label }) =>
            h('button', {
              class: `shape-btn ${store.style.shapeKind === kind ? 'is-selected' : ''}`,
              title: label,
              html: shapeIcon(kind),
              onclick: () => {
                store.setStyle({ shapeKind: kind })
                this.openToolPanel('shape', anchor)
              },
            }),
          ),
        ),
      )
      panel.append(h('h4', { style: 'margin-top:14px' }, ['Contorno']))
      panel.append(
        h(
          'div',
          { class: 'swatches' },
          INK_COLORS.map((c) =>
            h('button', {
              class: `swatch ${c.hex === store.style.shapeColor ? 'is-selected' : ''}`,
              style: `background:${c.hex}`,
              title: c.name,
              onclick: () => {
                store.setStyle({ shapeColor: c.hex })
                this.applyToSelection({ color: c.hex }, 'shape')
                closePopover()
              },
            }),
          ),
        ),
      )
      panel.append(h('h4', { style: 'margin-top:14px' }, ['Relleno']))
      panel.append(
        h(
          'div',
          { class: 'swatches' },
          FILL_COLORS.map((c) =>
            h('button', {
              class: `swatch ${c.hex === store.style.shapeFill ? 'is-selected' : ''} ${c.hex === 'none' ? 'is-none' : ''}`,
              style: c.hex === 'none' ? '' : `background:${c.hex}`,
              title: c.name,
              onclick: () => {
                store.setStyle({ shapeFill: c.hex })
                this.applyToSelection({ fill: c.hex }, 'shape')
                closePopover()
              },
            }),
          ),
        ),
      )
    }

    openPopoverAt(panel, { anchor, placement: this.panelPlacement })
  }

  /** Si hay algo seleccionado, el cambio de estilo también se aplica a la selección. */
  private applyToSelection(patch: Record<string, unknown>, onlyType?: string): void {
    const ids = this.app.store.selection
    if (ids.length === 0) return
    const board = this.app.board
    board.transact(() => {
      for (const id of ids) {
        const shape = board.get(id)
        if (!shape) continue
        if (onlyType && shape.type !== onlyType) continue
        if (!onlyType && shape.type !== 'draw') continue
        board.shapes.set(id, { ...shape, ...patch, updatedAt: Date.now() })
      }
    })
    this.app.renderer.invalidate()
  }

  private openReactions(e: MouseEvent): void {
    const anchor = e.currentTarget as HTMLElement
    const panel = h('div', {}, [
      h('h4', {}, ['Reacciones']),
      h(
        'div',
        { class: 'emoji-grid' },
        REACTIONS.map((emoji) =>
          h('button', { class: 'emoji-btn', onclick: () => { this.app.insertReaction(emoji); closePopover() } }, [emoji]),
        ),
      ),
    ])
    openPopoverAt(panel, { anchor, placement: this.panelPlacement })
  }

  private openMore(e: MouseEvent): void {
    const anchor = e.currentTarget as HTMLElement
    const menu = h('div', { class: 'menu-list' }, [
      menuItem('template', 'Plantillas', () => this.app.openTemplates()),
      menuItem('noteGrid', 'Cuadrícula de notas', () => this.app.insertNoteGrid()),
      menuItem('image', 'Insertar imagen', () => this.app.pickImage()),
      menuItem('link', 'Insertar enlace', () => this.app.insertLink()),
      h('div', { class: 'menu-sep' }),
      menuItem('palette', 'Fondo del lienzo', () => this.app.openBackgroundPicker()),
      menuItem('grid', 'Ajustar a la cuadrícula', () => {
        this.app.store.setPref('snapToGrid', !this.app.store.snapToGrid)
        this.app.toast(this.app.store.snapToGrid ? 'Ajuste a cuadrícula activado' : 'Ajuste a cuadrícula desactivado')
      }),
      h('div', { class: 'menu-sep' }),
      menuItem('download', 'Exportar como imagen', () => this.app.exportImage()),
      menuItem('keyboard', 'Atajos de teclado', () => this.app.openShortcuts()),
    ])
    openPopoverAt(menu, { anchor, placement: this.panelPlacement, align: 'end' })
  }

  private switchEl(on: boolean, onToggle: () => void): HTMLElement {
    return h('span', { class: `switch ${on ? 'is-on' : ''}`, onclick: onToggle })
  }

  private openMapLayout(anchor: HTMLElement): void {
    const { board } = this.app
    const item = (id: 'map' | 'right' | 'down', label: string, detalle: string): HTMLElement =>
      h(
        'button',
        {
          class: `menu-item ${board.mapLayout === id ? 'is-active' : ''}`,
          onclick: () => {
            setMapLayout(board, id)
            closePopover()
            this.app.zoomToFit()
          },
        },
        [h('b', {}, [label]), h('span', { style: 'color:var(--fg-3);font-size:12px' }, [` ${detalle}`])],
      )
    openPopoverAt(
      h('div', { class: 'menu' }, [
        item('map', 'Balanceado', 'ramas a ambos lados'),
        item('right', 'Horizontal', 'todo hacia la derecha'),
        item('down', 'Vertical', 'organigrama hacia abajo'),
      ]),
      { anchor, placement: this.panelPlacement },
    )
  }

  sync(): void {
    this.el.classList.toggle('is-top', this.app.store.toolbarTop)
    // Los botones de mapa solo aparecen en tableros de mapa mental.
    const esMapa = this.app.isMindMap
    for (const key of ['map-topic', 'map-layout']) {
      const btn = this.buttons.get(key)
      if (btn) btn.style.display = esMapa ? '' : 'none'
    }
    const { tool } = this.app.store
    const isLasso = this.app.isLassoActive()
    for (const [key, btn] of this.buttons) {
      const active =
        key === tool ? (key !== 'select' || !isLasso) : key === 'lasso' ? isLasso : false
      btn.classList.toggle('is-active', active)
    }
    // Los interruptores no son herramientas: marcan su propio estado.
    this.buttons.get('palm')?.classList.toggle('is-active', this.app.store.palmRejection)
    this.buttons.get('dark')?.classList.toggle('is-active', this.app.store.darkMode)
    const swatchMap: Record<string, string> = {
      draw: this.app.store.style.inkColor,
      highlighter: this.app.store.style.highlighterColor,
      note: this.app.store.style.noteColor,
      text: this.app.store.style.textColor,
      shape: this.app.store.style.shapeColor,
    }
    for (const [key, color] of Object.entries(swatchMap)) {
      const swatch = this.buttons.get(key)?.querySelector<HTMLElement>('.tool-swatch')
      if (swatch) swatch.style.background = color
    }
  }
}

function menuItem(iconName: string, label: string, onClick: () => void, shortcut?: string): HTMLElement {
  return h(
    'button',
    {
      class: 'menu-item',
      onclick: () => {
        closePopover()
        onClick()
      },
    },
    shortcut
      ? [spanIcon(iconName), label, h('span', { class: 'shortcut' }, [shortcut])]
      : [spanIcon(iconName), label],
  )
}

function spanIcon(name: string): HTMLElement {
  return h('span', { html: icon(name, 18), style: 'display:flex;color:var(--fg-3)' })
}

function shapeIcon(kind: GeoKind): string {
  const body: Record<GeoKind, string> = {
    rectangle: '<rect x="3.5" y="5.5" width="17" height="13" rx="1"/>',
    roundRectangle: '<rect x="3.5" y="5.5" width="17" height="13" rx="4"/>',
    ellipse: '<ellipse cx="12" cy="12" rx="8.5" ry="6.8"/>',
    triangle: '<path d="M12 4.5 20.5 19h-17z"/>',
    diamond: '<path d="M12 4 20 12l-8 8-8-8z"/>',
    hexagon: '<path d="M7.5 5h9l4.5 7-4.5 7h-9L3 12z"/>',
    star: '<path d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8z"/>',
    arrowRight: '<path d="M3 9.5h9V5.5l8 6.5-8 6.5v-4H3z"/>',
    line: '<path d="M4.5 18.5 19.5 5.5"/>',
    arrow: '<path d="M4.5 18.5 19.5 5.5"/><path d="M13.5 5.5h6v6"/>',
  }
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round">${body[kind]}</svg>`
}
